import { useEffect, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { fs } from "@/lib/firebase";
import type { JsonOrder } from "@/lib/types";
import type { ParsedQr } from "@/lib/qrParse";

const toMs = (ts: JsonOrder["createdAt"]): number => {
  if (typeof ts === "number") return ts;
  if (ts && typeof ts === "object" && "seconds" in ts) {
    return ts.seconds * 1000 + Math.floor(ts.nanoseconds / 1e6);
  }
  return 0;
};

/**
 * Listens to QRCode.order[] (the same array used by the tablet) and surfaces
 * the most recent orders, regardless of `source`. The PWA shows them all so
 * a returning user understands what's been ordered at the table during
 * their absence.
 */
export const useOrderHistory = (parsed: ParsedQr | null): JsonOrder[] => {
  const [orders, setOrders] = useState<JsonOrder[]>([]);

  useEffect(() => {
    if (!parsed) return;
    const ref = doc(fs, `Restaurant/${parsed.restoId}/QRCode/${parsed.qrId}`);
    const unsub = onSnapshot(ref, (snap) => {
      const data = snap.data();
      const arr = (data?.order as JsonOrder[] | undefined) || [];
      const sorted = [...arr].sort((a, b) => toMs(b.createdAt) - toMs(a.createdAt));
      setOrders(sorted);
    });
    return unsub;
  }, [parsed?.restoId, parsed?.qrId]);

  return orders;
};
